import React from 'react'
import styled, { css } from 'styled-components'
import { Range, Direction, getTrackBackground } from 'react-range'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faVolume } from '@fortawesome/pro-solid-svg-icons/faVolume'
import { faVolumeUp } from '@fortawesome/pro-solid-svg-icons/faVolumeUp'
import { faVolumeDown } from '@fortawesome/pro-solid-svg-icons/faVolumeDown'
import { faVolumeSlash } from '@fortawesome/pro-solid-svg-icons/faVolumeSlash'
import { colors } from 'utils/colors'

const STEP = 1
const MIN = 0
const MAX = 100

interface Props {
  onChange(value: number): void
  disabled?: boolean
}

const VolumeControl: React.FC<Props> = ({ onChange, disabled = false }) => {
  const [volume, setVolume] = React.useState(MAX)

  const volumeChange = (values: number[]) => {
    setVolume(values[0])
    onChange(values[0])
  }

  const setMaxVolume = () => {
    setVolume(MAX)
    onChange(MAX)
  }

  const toggleMute = () => {
    const toggled = volume === MIN ? MAX : MIN
    setVolume(toggled)
    onChange(toggled)
  }

  return (
    <Container>
      {!disabled && (
        <RangeWrapper id="volume-control-range">
          <button onClick={setMaxVolume}>
            <FontAwesomeIcon icon={faVolumeUp} color="white" />
          </button>
          <Range
            direction={Direction.Up}
            values={[volume]}
            step={STEP}
            min={MIN}
            max={MAX}
            onChange={volumeChange}
            renderTrack={({ props, children }) => (
              <Track onMouseDown={props.onMouseDown} onTouchStart={props.onTouchStart}>
                <TrackBackground
                  ref={props.ref}
                  background={getTrackBackground({
                    values: [volume],
                    colors: [colors.white, colors.gray6],
                    min: MIN,
                    max: MAX,
                    direction: Direction.Up,
                  })}
                >
                  {children}
                </TrackBackground>
              </Track>
            )}
            renderThumb={({ props }) => <Thumb {...props} />}
          />
          <button onClick={toggleMute}>
            <IconWrapper>
              {volume === 0 ? (
                <FontAwesomeIcon icon={faVolumeSlash} color="white" />
              ) : (
                <FontAwesomeIcon icon={faVolumeDown} color="white" />
              )}
            </IconWrapper>
          </button>
        </RangeWrapper>
      )}
      <IconWrapper>
        {volume === 0 ? (
          <FontAwesomeIcon icon={faVolumeSlash} color="white" />
        ) : (
          <FontAwesomeIcon icon={faVolume} color="white" />
        )}
      </IconWrapper>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  flex-direction: column;
  z-index: 2;

  --transition-volume: opacity 0.1s ease-in-out;

  & #volume-control-range {
    opacity: 0;
    transition: var(--transition-volume);
  }

  &:hover #volume-control-range {
    opacity: 1;
    transition: var(--transition-volume);
  }
`

const TrackHeight = '70px'

const RangeWrapper = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  padding: 10px 0;
  background: linear-gradient(180deg, #6f6f6f 0%, #424242 100%);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  transform: translateX(-50%);
  z-index: 1;
`

const Track = styled.div`
  height: ${TrackHeight};
  width: 10;
  display: flex;
  flex-grow: 1;
  cursor: pointer;
`

const TrackBackground = styled.div<{ background: string }>(
  (p) => css`
    width: 5px;
    height: 100%;
    border-radius: 4px;
    background: ${p.background};
    align-self: center;
  `,
)

const Thumb = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 15px;
  height: 15px;
  background: white;
  border-radius: 50%;
  border: 1px solid ${(p) => p.theme.colors.gray8};

  &:focus {
    outline: none;
  }
`

const IconWrapper = styled.div`
  box-sizing: border-box;
  margin: auto;
`

export default VolumeControl
